"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  HStack,
  NumberInput,
  NumberInputField,
  Box,
} from "@chakra-ui/react";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface ReminderMessageSettingsProps {
  triggerMs: number | undefined;
  maxCount: number | undefined;
  onChange: (triggerMs: number, maxCount: number) => Promise<boolean> | void;
}

export const ReminderMessageSettings: React.FC<
  ReminderMessageSettingsProps
> = ({ triggerMs = 10000, maxCount = 1, onChange }) => {
  const [seconds, setSeconds] = useState(Math.floor(triggerMs / 1000));
  const [count, setCount] = useState(maxCount);
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Update local state when props change
  useEffect(() => {
    setSeconds(Math.floor(triggerMs / 1000));
    setCount(maxCount);
  }, [triggerMs, maxCount]);

  const handleSecondsChange = (valueAsString: string) => {
    const value = Number.parseInt(valueAsString, 10);
    if (!isNaN(value)) {
      setSeconds(value);
    }
  };

  const handleCountChange = (valueAsString: string) => {
    const value = Number.parseInt(valueAsString, 10);
    if (!isNaN(value)) {
      setCount(value);
    }
  };

  const handleBlur = async () => {
    const newTriggerMs = seconds * 1000;
    if (newTriggerMs !== triggerMs || count !== maxCount) {
      setIsUpdating(true);
      try {
        await onChange(newTriggerMs, count);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <FormControl>
      <FormLabel
        fontSize="sm"
        fontWeight="medium"
        color={isDark ? "gray.300" : "gray.700"}
        mb={1}
      >
        Reminder Message Frequency
      </FormLabel>
      <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"} mb={2}>
        Control how often AI will send a reminder message.
      </Text>
      <HStack spacing={4}>
        <Box>
          <NumberInput
            min={1}
            max={60}
            value={seconds}
            onChange={handleSecondsChange}
            onBlur={handleBlur}
            isDisabled={isUpdating}
            size="sm"
            w="80px"
          >
            <NumberInputField
              size="sm"
              bg={isDark ? "gray.700" : "white"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              _focus={{
                borderColor: isDark ? "gray.500" : "gray.400",
                boxShadow: "none",
              }}
            />
          </NumberInput>
          <Text
            fontSize="xs"
            color={isDark ? "gray.400" : "gray.600"}
            mt={1}
            textAlign="center"
          >
            seconds
          </Text>
        </Box>
        <Box>
          <NumberInput
            min={0}
            max={10}
            value={count}
            onChange={handleCountChange}
            onBlur={handleBlur}
            isDisabled={isUpdating}
            size="sm"
            w="80px"
          >
            <NumberInputField
              bg={isDark ? "gray.700" : "white"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              size="sm"
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              _focus={{
                borderColor: isDark ? "gray.500" : "gray.400",
                boxShadow: "none",
              }}
            />
          </NumberInput>
          <Text
            fontSize="xs"
            color={isDark ? "gray.400" : "gray.600"}
            mt={1}
            textAlign="center"
          >
            times
          </Text>
        </Box>
      </HStack>
    </FormControl>
  );
};
